/**
 * Enterprise-style community dashboard page orchestrator.
 *
 * Mounted as the "Dashboard" tab inside /communitea (see Community.tsx).
 * Renders a sticky time-window dropdown and composes the nine data sections.
 */
import React, { useEffect } from "react";
import { setDocumentTitle, setDefaultSiteOg } from "util/ogMetadataUtils";
import ScrollHideContainer from "ui/ScrollHideContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/InputSelect";
import { useCommunityDashboard } from "./hooks/useCommunityDashboard";
import EnterprisePulse from "./sections/EnterprisePulse";
import ChangePipeline from "./sections/ChangePipeline";
import ActivityTrend from "./sections/ActivityTrend";
import PriorityFocus from "./sections/PriorityFocus";
import GovernanceHealth from "./sections/GovernanceHealth";
import CommunityAlignment from "./sections/CommunityAlignment";
import TopContributors from "./sections/TopContributors";
import GlobalPresence from "./sections/GlobalPresence";
import OpenOpportunities from "./sections/OpenOpportunities";
import {
  COMMUNITY_WINDOW_OPTIONS,
  formatCommunityWindow,
} from "./utils";

const CommunityDashboardPage: React.FC = () => {
  const {
    days,
    setDays,
    labels,
    numberFormatter,
    compactNumberFormatter,
    dateFormatter,
    regionDisplayNames,
    overviewQuery,
    pipelineQuery,
    activityTrendQuery,
    issueActivityQuery,
    leaderboardQuery,
    governanceQuery,
    alignmentQuery,
    demographicsQuery,
  } = useCommunityDashboard();

  useEffect(() => {
    setDocumentTitle(labels.pageTitle);
    setDefaultSiteOg({ title: labels.pageTitle, url: window.location.href });
  }, [labels.pageTitle]);

  return (
    <div className="mx-auto max-w-7xl pb-16">
      {/* Screen-reader-only page heading: the visible chrome title sits in
          the parent Communitea page header, so we don't render a duplicate
          floating H1 here. */}
      <h1 className="sr-only">{labels.pageTitle}</h1>

      <ScrollHideContainer
        className="z-40 max-w-7xl bg-eoe-bg/95 backdrop-blur-sm dark:bg-eoe-dark-bg/95"
        hideOffset={57}
        threshold={56}
      >
        <div className="flex items-center justify-start px-4 pb-3 pt-3 md:px-6">
          <Select
            value={String(days)}
            onValueChange={(value: string) => {
              const parsed = Number.parseInt(value, 10);
              if (Number.isFinite(parsed)) {
                setDays(parsed);
              }
            }}
          >
            <SelectTrigger aria-label={labels.windowDropdownLabel} className="px-3 py-2">
              <SelectValue placeholder={formatCommunityWindow(labels.windowDays, days)} />
            </SelectTrigger>
            <SelectContent>
              {COMMUNITY_WINDOW_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {formatCommunityWindow(labels.windowDays, option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </ScrollHideContainer>

      <div className="space-y-6 px-4 pt-2 md:px-6">
        <EnterprisePulse
          query={overviewQuery}
          labels={labels}
          numberFormatter={numberFormatter}
        />
        <ChangePipeline
          query={pipelineQuery}
          labels={labels}
          numberFormatter={numberFormatter}
        />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_1fr]">
          <ActivityTrend
            query={activityTrendQuery}
            labels={labels}
            compactNumberFormatter={compactNumberFormatter}
            dateFormatter={dateFormatter}
          />
          <CommunityAlignment
            query={alignmentQuery}
            labels={labels}
            numberFormatter={numberFormatter}
          />
        </div>
        <PriorityFocus
          query={issueActivityQuery}
          labels={labels}
          numberFormatter={numberFormatter}
        />
        <GovernanceHealth
          query={governanceQuery}
          labels={labels}
          numberFormatter={numberFormatter}
        />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_1fr]">
          <TopContributors
            query={leaderboardQuery}
            labels={labels}
            numberFormatter={numberFormatter}
          />
          <GlobalPresence
            query={demographicsQuery}
            labels={labels}
            numberFormatter={numberFormatter}
            regionDisplayNames={regionDisplayNames}
          />
        </div>
        <OpenOpportunities
          issueActivityQuery={issueActivityQuery}
          governanceQuery={governanceQuery}
          labels={labels}
        />
      </div>
    </div>
  );
};

export default CommunityDashboardPage;
